import React from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {useParams} from "react-router-dom";
import {isWebView} from "../isWebView";
import {tipAmounts, toTipCurrency} from "../model/constants";
import {rateDelivery, RecipientStopInfo, RecipientTrackingPageParams} from "../model/rideInfo";
import {getLanguage} from "../translations";
import {CallCourierDialog} from "./CallCourierDialog";
import {PudoInfo} from "./PudoInfo";
import {Feedback} from "./Feedback";
import "../styles/InfoPanel.css";
import {StopCancel} from "./StopCancel";
import {StopStatusInfo} from "./StopStatusInfo";
import {getPrivacyPolicyUrl} from "./links";
import "../styles/MapPageView.css";
import {MessageToCourier} from "./MessageToCourier";
import {SectionWrapper} from "./SectionWrapper";
import {SenderInfo} from "./SenderInfo";
import {Tips} from "./Tips";
import sanitizeHtml from "sanitize-html";
import {DriverInfoCard} from "./DriverInfoCard";

interface PackageInfoPanelProps {
    stop: RecipientStopInfo;
    onModalToggle: (open: boolean) => void;
}

export function PackageInfoPanel({stop, onModalToggle}: PackageInfoPanelProps) {
    const {shareKey} = useParams<RecipientTrackingPageParams>();
    const intl = useIntl();

    const isInQueue = stop?.status === "IN_QUEUE";
    const isDelivering = stop?.status === "DELIVERING";
    const isArrived = stop?.status === "ARRIVED";
    const isDelivered = stop?.status === "DELIVERED";
    const isCancelled = stop?.status === "CANCELLED";
    const isRedirected = stop?.status === "REDIRECTED";
    const isDeliveredToPudo = stop?.status === "DELIVERED_TO_PUDO";

    const displayCarInfo = isInQueue || isDelivering || isArrived || isRedirected;
    const displayDepotInfo = isDeliveredToPudo;
    const displayFeedback = isDelivered || isDeliveredToPudo || isCancelled;
    const displayTip =
        isInQueue || isDelivering || isArrived || isRedirected || isDeliveredToPudo || isDelivered;
    const tipCurrency = toTipCurrency(stop?.tipCurrency);

    const verificationCode = isDeliveredToPudo ? undefined : stop?.verificationCode;

    return (
        <div className="info-panel">
            {stop != null && (
                <>
                    <SectionWrapper>
                        <h3 className="package-status">
                            <StopStatusInfo stop={stop} />
                        </h3>

                        <SenderInfo
                            code={verificationCode}
                            sender={stop.packageInfo}
                            destination={stop.pudo?.location ?? stop.destinationLocation}
                        />

                        {displayCarInfo && stop.driver && stop.carInfo && (
                            <DriverInfoCard driver={stop.driver} car={stop.carInfo} />
                        )}

                        <div className="stop-action-group">
                            {stop?.messageToDriverEnabled && (
                                <MessageToCourier receivedMessage={stop?.messageToDriver} />
                            )}

                            {displayCarInfo && stop?.driver?.phoneNumber && (
                                <CallCourierDialog
                                    phoneNumber={stop.driver.phoneNumber}
                                    codeForCommunication={stop.codeForCommunication}
                                    ivrEnabled={stop.driver.ivrEnabled}
                                    maskedNumber={stop.driver.maskedNumber}
                                />
                            )}

                            {stop?.cancellable && stop?.cancelReasons && (
                                <StopCancel
                                    cancelReasons={stop.cancelReasons}
                                    redirectToPudoEnabled={stop.redirectToPudoEnabled}
                                    onStatusChange={onModalToggle}
                                    ordererName={stop.ordererName}
                                    senderName={stop.packageInfo?.senderInfo?.name}
                                />
                            )}
                        </div>

                        {displayDepotInfo && <PudoInfo stop={stop} />}
                    </SectionWrapper>
                    {displayFeedback && (
                        <SectionWrapper>
                            <Feedback
                                shareKey={shareKey}
                                givenRating={stop.rating}
                                title={intl.formatMessage({id: "how_was_the_delivery"})}
                                indications={stop.ratingIndications}
                                rateFunction={rateDelivery}
                            />
                        </SectionWrapper>
                    )}
                    {displayTip && (
                        <SectionWrapper>
                            <Tips
                                tipAmounts={tipAmounts[tipCurrency]["DELIVERY_STOP"]}
                                currency={tipCurrency}
                                shareKey={shareKey}
                                shareKeyType="DELIVERY_STOP"
                            />
                        </SectionWrapper>
                    )}
                    {stop.businessCustomizationHtml && (
                        <SectionWrapper>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(stop.businessCustomizationHtml),
                                }}
                            ></div>
                        </SectionWrapper>
                    )}
                </>
            )}
            {!isWebView() && !stop?.businessCustomizationHtml && (
                <div className="secondary-section">
                    {stop != null && (
                        <div className="privacy-policy">
                            <a href={getPrivacyPolicyUrl(getLanguage())} className="privacy-link">
                                <FormattedMessage id="privacy_policy" />
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
